import BaseApi from '@core/api/model';

import { Account, Investor } from '@/models';

export class AccountApi extends BaseApi {
    static resource = 'accounts';

    get endpoints() {
        return {
            ...super.endpoints,
            getBalances: this.getBalances.bind(this),
            getCustodyAccounts: this.getCustodyAccounts.bind(this),
            getCustodyAccount: this.getCustodyAccount.bind(this),
            postCustodyAccount: this.postCustodyAccount.bind(this),
            patchCustodyAccount: this.patchCustodyAccount.bind(this),
            removeCustodyAccount: this.removeCustodyAccount.bind(this)
        };
    }

    async getBalances(id, filters = {}) {
        return await BaseApi.api.get(`${this.base_url}/${id}/balances`, filters);
    }

    async getCustodyAccounts(account_id, config = {}) {
        const response = await BaseApi.api.get(`${this.base_url}/${account_id}/custody-accounts`, {}, config);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async getCustodyAccount(account_id, custody_account_id, params = {}, options = {}) {
        const response = await BaseApi.api.get(
            `${this.base_url}/${account_id}/custody-accounts/${custody_account_id}`,
            params,
            options
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async postCustodyAccount(account_id, payload = {}) {
        if (!account_id) {
            throw new Error('Missing account_id on POST custody account', payload);
        }

        const response = await BaseApi.api.post(`${this.base_url}/${account_id}/custody-accounts`, {
            reference: payload.reference,
            joint_account: payload.joint_account,
            status: payload.status,
            custodian_id: payload.custodian_id
        });

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            const account = Account.find(account_id);

            if (account) {
                let custodyAccounts = account.custody_accounts || [];
                const existingIndex = custodyAccounts.findIndex(ca => ca.id === response.data.id);

                if (existingIndex > -1) {
                    custodyAccounts[`${existingIndex}`] = response.data;
                } else {
                    custodyAccounts.push(response.data);
                }

                this.$updateCustodyAccountCache(account, custodyAccounts);
            }
        });
    }

    async patchCustodyAccount(account_id, custody_account_id, payload = {}) {
        if (!account_id) {
            throw new Error('Missing account_id on PATCH custody account', payload);
        }
        if (!custody_account_id) {
            throw new Error('Missing custody_account_id on PATCH custody account', payload);
        }

        const response = await BaseApi.api.patch(
            `${this.base_url}/${account_id}/custody-accounts/${custody_account_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            // const account = Account.find(account_id);

            // if (account) {
            //     let custodyAccounts = account.custody_accounts || [];
            //     const existingIndex = custodyAccounts.findIndex(ca => ca.id === response.data.id);

            //     if (existingIndex > -1) {
            //         custodyAccounts[`${existingIndex}`] = response.data;
            //     } else {
            //         custodyAccounts.push(response.data);
            //     }

            //     this.$updateCustodyAccountCache(account, custodyAccounts);
            // }
        });
    }

    async removeCustodyAccount(account_id, custody_account_id) {
        if (!account_id) {
            throw new Error('Missing account_id on DELETE custody account');
        }
        if (!custody_account_id) {
            throw new Error('Missing custody_account_id on DELETE custody account');
        }

        const response = await BaseApi.api.remove(
            `${this.base_url}/${account_id}/custody-accounts/${custody_account_id}`
        );

        return this._respond(response, () => {
            const account = Account.find(account_id);

            if (account) {
                let custodyAccounts = account.custody_accounts || [];
                custodyAccounts = custodyAccounts.filter(ca => ca.id !== custody_account_id);

                this.$updateCustodyAccountCache(account, custodyAccounts);
            }
        });
    }

    $updateCustodyAccountCache(account, custodyAccounts) {
        if (!account) {
            return;
        }

        custodyAccounts = Array.isArray(custodyAccounts) ? custodyAccounts : [custodyAccounts];

        Account.update({
            where: account.id,
            data: { custodyAccounts }
        });

        const investor = Investor.query()
            .with('accounts', query => {
                query.where('id', account.id);
            })
            .first();

        if (investor) {
            investor.accounts = investor.accounts.map(acc => {
                if (acc.id === account.id) {
                    acc.custody_accounts = custodyAccounts;
                }
                return acc;
            });

            Investor.update({ where: investor.id, data: investor });
        }
    }
}

export default AccountApi;
